<template>
  <Combobox as="div" :model-value="selected" @update:modelValue="handleChange" v-slot="{ open }">
    <ComboboxLabel
      class="block text-xs font-semibold py-[6px] text-typography-primary truncate subtext-text"
      v-if="label"
    >
      {{ label }} {{ badge }}
      <span v-if="required" class="text-red-600">*</span>
    </ComboboxLabel>
    <div>
      <ComboboxButton
        class="w-full cursor-default rounded-md bg-neutral-white text-left text-gray-900 sm:text-sm sm:leading-6 flex items-center justify-between px-4 gap-2"
        :class="{
          'opacity-50': disabled,
          'ring-2 ring-danger-700': error,
          'primary-gradient font-normal primary-gradient hover:bg-primary-800 active:bg-primary-900 pulse-primary-button h-[36px]':
            variant === 'primary',
          'h-[44px]': variant !== 'primary' && variant !== 'ghost',

          'ring-1 ring-inset ring-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-700': !error,
          'ring-neutral-white focus:ring-neutral-white': variant === 'ghost',
        }"
        :disabled="disabled"
        ref="trigger"
        v-bind="$attrs"
        v-hubble="'trigger'"
      >
        <slot name="button-icon" />

        <StatusBadge v-if="selected?.badge" :low-contrast="true" :type="selected.status" class="badge">
          {{ selected.badge }}
        </StatusBadge>

        <span
          v-else
          :class="{
            'text-white': variant === 'primary',
          }"
          class="block truncate text-typography-primary"
          :title="selected?.title"
        >
          {{ selected?.label || placeholder }}
        </span>
        <span class="flex items-center">
          <span
            :class="{
              hidden: variant === 'primary',
            }"
            class="pointer-events-none"
          >
            <ChevronDownIcon class="h-4 w-4 text-typography-primary" aria-hidden="true" v-if="!open" />
            <ChevronUpIcon class="h-4 w-4 text-typography-primary" aria-hidden="true" v-else />
          </span>
        </span>
      </ComboboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        @enter="handleEnter"
      >
        <div
          class="border rounded-md border-neutral-200 flex flex-col bg-neutral-white shadow-md z-10"
          ref="menu"
          v-if="open"
        >
          <div class="flex-none flex items-center gap-2" v-if="searchable">
            <ComboboxInput
              placeholder="Search..."
              class="px-3 py-2 border-b border-neutral-200 bg-neutral-white w-full outline-none"
              @change="search = $event.target.value"
              ref="input"
              :on-vnode-before-unmount="() => (search = '')"
            />
            <XMarkIcon
              class="text-typography-secondary h-6 w-6 cursor-pointer"
              @click="search = ''"
              v-if="search != ''"
            />
          </div>
          <div class="flex-1 overflow-auto">
            <ComboboxOptions
              v-hubble="'menu'"
              class="max-h-60 rounded-md bg-neutral-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm overflow-auto"
            >
              <ComboboxOption
                as="template"
                v-for="item in visibleItems"
                :key="item.value"
                :value="item"
                v-slot="{ active, selected }"
                v-hubble="`option-${item.value}`"
              >
                <li
                  :class="{
                    'bg-neutral-200 ': active,
                    'text-typography-primary  cursor-default select-none py-2 px-3 flex items-center justify-between': true,
                    'bg-primary-100': selected,
                  }"
                  :aria-selected="selected"
                  :title="item.title"
                >
                  <span v-if="item.label" :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{
                    item.label
                  }}</span>
                  <StatusBadge v-if="item.badge" :low-contrast="true" :type="item.status" class="badge">
                    {{ item.badge }}
                  </StatusBadge>

                  <span
                    v-if="selected"
                    :class="[active ? 'text-white bg-primary-100' : 'text-primary-700', 'flex items-center']"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </div>
        </div>
      </transition>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import { createPopper } from '@popperjs/core';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/vue/20/solid';
import { type PropType, ref, computed, watch } from 'vue';

const trigger = ref<any>(null);
const menu = ref<any>(null);
const search = ref('');
const input = ref<any>(null);

interface iDropdownItem {
  value: any;
  label: string;
  /** Optional native hover tooltip for the option and the selected value. */
  title?: string;
}

const props = defineProps({
  error: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array as PropType<iDropdownItem[]>,
    required: true,
  },
  name: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Select option',
  },
  label: {
    type: String,
    default: null,
  },
  badge: {
    type: String,
    default: null,
  },
  modelValue: {
    type: [String, Number, Boolean, Object, Array] as PropType<any>,
    default: null,
  },
  required: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: null,
  },
  popperPlacement: {
    type: String,
    default: 'bottom-start',
  },
  dropdownWidth: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const selected = computed(() => {
  return props.items.find(item => item.value === props.modelValue);
});

const visibleItems = computed(() => {
  if (!search.value) return props.items;

  return props.items.filter(item => item.label.toLowerCase().includes(search.value.toLowerCase()));
});

function handleChange(item: iDropdownItem) {
  emit('update:modelValue', item.value);
}

watch(
  () => input.value,
  () => {
    if (!input.value) return;

    search.value = '';
    input.value.el.focus();
  },
);

watch(
  () => visibleItems.value,
  () => {
    scrollSelectedOptionIntoView();
  },
);

function initPopper() {
  const popper = createPopper(trigger.value.el, menu.value, {
    placement: props.popperPlacement || 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      {
        effect: ({ state }) => {
          state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = props.dropdownWidth || `${state.rects.reference.width}px`;
        },
        name: 'sameWidth',
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'bottom'],
        },
      },
    ],
    strategy: 'fixed',
  });

  popper.forceUpdate();
}

function handleEnter() {
  initPopper();
  scrollSelectedOptionIntoView();
}

function scrollSelectedOptionIntoView() {
  const selectedEl = menu.value?.querySelector('[aria-selected="true"]');

  if (selectedEl) {
    selectedEl.scrollIntoView();
  }
}
</script>
